import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateWorkTravelZoneMutation, useUpdateWorkTravelZoneMutation } from '../../../../hooks/mutations/useWorkTravelZoneMutation';
import { WorkTravelZone } from '../../../../services/apiSLTP/sites/workTravelZones';
import CustomTextInput from '../../../ui/CustomTextInput';
import ModalBody from '../../ModalBody';

interface ModalProps {
    workTravelZone: WorkTravelZone | null;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    showEditModal: boolean;
    refetch: () => void;
}

const UpdateWorkTravelZoneModal: React.FC<ModalProps> = ({ workTravelZone, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [name, setName] = useState(workTravelZone?.name || '');
    const [shortName, setShortName] = useState(workTravelZone?.shortName || '');
    const [minDistance, setMinDistance] = useState(workTravelZone?.minDistance?.toString() || '');
    const [maxDistance, setMaxDistance] = useState(workTravelZone?.maxDistance?.toString() || '');
    const [travelPrice, setTravelPrice] = useState(workTravelZone?.travelPrice?.toString() || '');
    const [payrollCode, setPayrollCode] = useState(workTravelZone?.payrollCode || '');

    const { mutate: updateWorkTravelZone } = useUpdateWorkTravelZoneMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => console.error('Error updating work travel zone:', error.message),
    });

    const { mutate: createWorkTravelZone } = useCreateWorkTravelZoneMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => console.error('Error creating work travel zone:', error.message),
    });

    const isValidForm =
        name.trim() !== '' &&
        shortName.trim() !== '' &&
        payrollCode.trim() !== '' &&
        !isNaN(Number(minDistance)) &&
        !isNaN(Number(maxDistance)) &&
        !isNaN(Number(travelPrice)) &&
        Number(minDistance) >= 0 &&
        Number(maxDistance) >= Number(minDistance) &&
        Number(travelPrice) >= 0;

    const handleSubmit = () => {
        const payload = {
            name: name.trim(),
            shortName: shortName.trim(),
            minDistance: Number(minDistance),
            maxDistance: Number(maxDistance),
            travelPrice: Number(travelPrice),
            payrollCode: payrollCode.trim(),
            crudStatus: crudStatusEnum.OK,
        };

        if (isEditMode && workTravelZone?._id) {
            updateWorkTravelZone({ id: workTravelZone._id, data: payload });
        } else {
            createWorkTravelZone(payload);
        }
    };

    return (
        <ModalBody
            title={t('workTravelZone')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!isValidForm}
            onSubmit={handleSubmit}
        >
            <CustomTextInput label={t('name')} value={name} onChange={setName} required />
            <CustomTextInput label={t('shortName')} value={shortName} onChange={setShortName} required />
            <CustomTextInput label={t('minDistance')} value={minDistance} onChange={setMinDistance} type="number" required />
            <CustomTextInput label={t('maxDistance')} value={maxDistance} onChange={setMaxDistance} type="number" required />
            <CustomTextInput label={t('price')} value={travelPrice} onChange={setTravelPrice} type="number" required />
            <CustomTextInput label={t('payrollCode')} value={payrollCode} onChange={setPayrollCode} required />
        </ModalBody>
    );
};

export default UpdateWorkTravelZoneModal;
